import { useState } from "react";
import { Eye, EyeOff, Download, ExternalLink, FileText, Film, Image as ImageIcon, Link2, Pencil, Trash2, Check } from "lucide-react";
import { fileSize } from "@/utils/chunkedUpload";

const API = process.env.REACT_APP_BACKEND_URL;
const ext = (f) => (f || "").split(".").pop().toUpperCase();
const iconFor = (m) => (m.kind === "link" ? Link2 : m.is_video ? Film : /PNG|JPE?G|GIF|WEBP/.test(ext(m.filename)) ? ImageIcon : FileText);

export const MaterialRow = ({ m, lessons, onPatch, onDelete, LessonSelect }) => {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(m.title);
  const Icon = iconFor(m);
  const save = () => { setEditing(false); if (title.trim() && title !== m.title) onPatch({ title }); else setTitle(m.title); };

  return (
    <div className={`flex flex-wrap items-center gap-3 px-4 py-2.5 ${m.hidden ? "bg-slate-50" : ""}`} data-testid={`material-${m.id}`}>
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${m.hidden ? "bg-slate-200 text-slate-500" : "bg-green-50 text-green-700"}`}><Icon size={16} /></span>
      <div className="min-w-0 flex-1">
        {editing ? (
          <div className="flex items-center gap-1">
            <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === "Enter" && save()} className="h-7 w-full rounded border border-border px-2 text-sm" data-testid={`material-title-input-${m.id}`} />
            <Check size={14} className="text-green-700" />
          </div>
        ) : (
          <p className={`truncate text-sm font-medium ${m.hidden ? "text-slate-400 line-through" : "text-slate-900"}`}>{m.title}</p>
        )}
        <p className="text-[11px] text-muted-foreground">
          {m.kind === "link" ? m.url : `${ext(m.filename)} · ${fileSize(m.size)}`}
          {m.hidden && <span className="ml-2 font-medium text-amber-700">Hidden from learners</span>}
        </p>
      </div>
      <LessonSelect lessons={lessons} value={m.lesson_id} onChange={(v) => onPatch({ lesson_id: v })} testId={`material-move-${m.id}`} className="max-w-[200px]" />
      <div className="flex items-center gap-1">
        <button onClick={() => setEditing(true)} className="rounded p-1.5 text-slate-500 hover:bg-slate-100" title="Rename" data-testid={`material-rename-${m.id}`}><Pencil size={13} /></button>
        <button onClick={() => onPatch({ hidden: !m.hidden })} className="rounded p-1.5 text-slate-500 hover:bg-slate-100" title={m.hidden ? "Show to learners" : "Hide from learners"} data-testid={`material-toggle-hidden-${m.id}`}>
          {m.hidden ? <EyeOff size={13} /> : <Eye size={13} />}
        </button>
        {m.kind === "link"
          ? <a href={m.url} target="_blank" rel="noreferrer" className="rounded p-1.5 text-slate-500 hover:bg-slate-100" title="Open" data-testid={`material-open-${m.id}`}><ExternalLink size={13} /></a>
          : <a href={`${API}/api/lms/admin/resources/${m.id}/download`} className="rounded p-1.5 text-slate-500 hover:bg-slate-100" title="Download" data-testid={`material-download-${m.id}`}><Download size={13} /></a>}
        <button onClick={onDelete} className="rounded p-1.5 text-rose-500 hover:bg-rose-50" title="Delete" data-testid={`material-delete-${m.id}`}><Trash2 size={13} /></button>
      </div>
    </div>
  );
};
